import React, { useRef, useState, DragEvent, ChangeEvent, useEffect } from "react";
import { createFoldername, fetchFiles, fileUpload } from "./action";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import AuthHeader from "src/components/@auth/auth-layout/auth-header";
import AppFooter from "src/layouts/footer";
import PrimaryButton from "src/components/reusable/button/primary-button";

type FileItem = {
  name: string;
  type: "file" | "folder";
};

const FileManager: React.FC = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [fileError, setFileError] = useState("");

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [submittingAction, setSubmittingAction] = useState(false);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles([...selectedFiles, ...files]);
    setFileError(""); // Clear file error on new selection
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    setSelectedFiles([...selectedFiles, ...files]);
    setFileError(""); // Clear file error on new selection
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const [searchParams] = useSearchParams();

  const handleSubmitCreateFolder = () => {
    let hasError = false;

    if (selectedFiles.length === 0) {
      setFileError("Please select at least one file.");
      hasError = true;
    } else {
      setFileError("");
    }

    if (hasError) return;

    setSubmittingAction(true);

    // Now use folderName and selectedFiles
    fileUpload(searchParams.get("folder") || "folder", selectedFiles, ({ success, errorType }) => {
      setSubmittingAction(false);

      if (success) {
        setSelectedFiles([]);
        getFolderFiles();
        if (fileInputRef.current) fileInputRef.current.value = "";
        toast.success("File uploaded");
      }
    });
  };

  useEffect(() => {
    const accessGranted = localStorage.getItem("dropboxAccess");
    if (accessGranted !== "granted") {
      navigate("/dropbox-login", { replace: true });
    }
  }, []);

  const getFolderFiles = () => {
    fetchFiles(searchParams.get("folder") || "folder", ({ success, data }) => {
      if (success) {
        setItems(data || []);
      }
    });
  };

  useEffect(() => {
    getFolderFiles();
  }, [searchParams.get("folder")]);

  return (
    <>
      <div className="p-6 max-w-4xl mx-auto font-sans min-h-[calc(100vh-88px)]">
        {/* Action Buttons */}

        {/* Dropzone */}
        <div className="text-lg text-gray-600 mb-4">
          <span
            onClick={() => navigate("/dropbox")}
            className="text-blue-600 cursor-pointer hover:underline"
          >
            File Manager
          </span>
          {searchParams.get("folder") && (
            <>
              <span className="mx-1">{">"}</span>
              <span className="font-medium text-gray-800">{searchParams.get("folder")}</span>
            </>
          )}
        </div>
        <div className="mb-5">
          <div
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-blue-50 cursor-pointer"
          >
            <p className="text-center text-base">Drag and Drop files to upload</p>
            <p className="text-base py-0.5">or</p>
            <p className="text-primary-900 underline cursor-pointer hover:text-primary-800 transition duration-300 ease-in-out text-sm">
              Browse
            </p>
          </div>
          {fileError && <div className="text-xs text-danger-500 mb-3">{fileError}</div>}
        </div>
        {/* Hidden File Input */}
        <input
          type="file"
          multiple
          onChange={handleFileChange}
          ref={fileInputRef}
          style={{ display: "none" }}
        />

        {/* Selected Files List */}
        {selectedFiles.length > 0 && (
          <div className="mb-4 border border-gray-200 rounded p-3">
            <p className="text-sm font-semibold mb-2">Selected Files:</p>
            <ul className="space-y-2">
              {selectedFiles.map((file, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between bg-gray-100 rounded p-2 hover:bg-gray-200 transition"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{file.name}</span>
                    <span className="text-xs text-gray-600 ml-2">
                      ({(file.size / 1024).toFixed(2)} KB)
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    ✖
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <PrimaryButton
          onClick={handleSubmitCreateFolder}
          text={submittingAction ? "Uploading..." : "Upload"}
          loading={submittingAction}
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={16}
              height={16}
              viewBox="0 0 49 48"
              fill="none"
            >
              <path
                fill="white"
                fillRule="evenodd"
                d="M22.748.76A2.389 2.389 0 0 1 24.5 0c.615 0 1.229.234 1.7.704l11.996 11.997a2.4 2.4 0 1 1-3.393 3.395l-7.904-7.904v23.003a2.4 2.4 0 1 1-4.8 0V8.197l-7.9 7.9a2.4 2.4 0 0 1-3.392-3.396L22.747.761ZM2.903 28.803c1.326 0 2.401 1.075 2.401 2.4V40.8A2.397 2.397 0 0 0 7.703 43.2h33.593a2.4 2.4 0 0 0 2.4-2.398v-9.599a2.4 2.4 0 1 1 4.8 0V40.8a7.2 7.2 0 0 1-7.2 7.2H7.703c-1.91 0-3.74-.758-5.09-2.108A7.196 7.196 0 0 1 .503 40.8v-9.598c0-1.325 1.075-2.4 2.4-2.4Z"
                clipRule="evenodd"
              />
            </svg>
          }
        />

        {items.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-semibold mb-4">Uploaded Files</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200 rounded-md overflow-hidden text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left px-4 py-2 border-b">#</th>
                    <th className="text-left px-4 py-2 border-b">Filename</th>
                    <th className="text-left px-4 py-2 border-b">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-2 border-b">{index + 1}</td>
                      <td className="px-4 py-2 border-b">{item}</td>
                      <td className="px-4 py-2 border-b capitalize">
                        {item?.split(".")?.pop()?.toLowerCase() || "File"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      <AppFooter />
    </>
  );
};

export default FileManager;
